import {
    useCallback,
    useEffect,
    useState,
    Fragment,
    useMemo,
    useRef
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    FlatList,
    Image,
    View
} from "react-native";
import type {
    MemberEditModalProps,
    CompanySelectionType
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    DateTimePicker,
    BottomSheet,
    SelectBox,
    TextInput,
    Seperator,
    Button,
    Switch,
    Dialog,
    Modal,
    Text
} from "ncore-ui-kit";
import type {
    GetAllCompaniesForAdminObjectType
} from "../../../../services/restAPI/actions/getAllCompaniesForAdmin/type";
import type {
    GetCitiesResponseType
} from "../../../../services/restAPI/actions/getCities/type";
import type {
    GetDepartmentsResponseType
} from "../../../../services/restAPI/actions/getDepartments/type";
import type {
    GetDistrictsResponseType
} from "../../../../services/restAPI/actions/getDistricts/type";
import type {
    GetFacultiesResponseType
} from "../../../../services/restAPI/actions/getFaculties/type";
import type {
    GetPositionsResponseType
} from "../../../../services/restAPI/actions/getPositions/type";
import type {
    GetUniversitiesResponseType
} from "../../../../services/restAPI/actions/getUniversities/type";
import type {
    RegisterUserRequestType,
    UserHomeAddressRequest,
    UserEducationRequest
} from "../../../../services/restAPI/actions/registerUser/type";
import {
    PencilIcon,
    Trash2Icon,
    PlusIcon,
    SaveIcon,
    XIcon
} from "lucide-react-native";
import {
    emailValidation,
    phoneValidation,
    tckValidation
} from "../../../../utils/validations";
import RESTServiceContext from "../../../../services/restAPI/context";

const MemberEditModal = ({
    memberData,
    isVisible,
    onSuccess,
    onClose
}: MemberEditModalProps) => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const [
        formData,
        setFormData
    ] = useState<Partial<RegisterUserRequestType>>({
        existingCompanies: [],
        isStudent: false,
        newCompanies: [],
        education: []
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const [
        isFormReady,
        setIsFormReady
    ] = useState<boolean>(false);

    const [
        positions,
        setPositions
    ] = useState<GetPositionsResponseType["payload"]>([]);

    const [
        logoPreview,
        setLogoPreview
    ] = useState<string>("");

    const [
        logoFile,
        setLogoFile
    ] = useState<File | null>(null);

    const logoInputRef = useRef<HTMLInputElement | null>(null);

    // Company selection states
    const [
        isCompanySheetVisible,
        setIsCompanySheetVisible
    ] = useState<boolean>(false);

    const [
        cities,
        setCities
    ] = useState<GetCitiesResponseType["payload"]>([]);

    const [
        districts,
        setDistricts
    ] = useState<GetDistrictsResponseType["payload"]>([]);

    const [
        universities,
        setUniversities
    ] = useState<GetUniversitiesResponseType["payload"]>([]);

    const [
        faculties,
        setFaculties
    ] = useState<GetFacultiesResponseType["payload"]>([]);

    const [
        departments,
        setDepartments
    ] = useState<GetDepartmentsResponseType["payload"]>([]);

    const [
        isEducationSheetVisible,
        setIsEducationSheetVisible
    ] = useState<boolean>(false);

    const [
        newEducationDraft,
        setNewEducationDraft
    ] = useState<UserEducationRequest | null>(null);

    const [
        editEducationIndex,
        setEditEducationIndex
    ] = useState<number | null>(null);

    const [
        companies,
        setCompanies
    ] = useState<GetAllCompaniesForAdminObjectType[]>([]);

    const [
        companySearchText,
        setCompanySearchText
    ] = useState<string>("");

    const [
        companyPage,
        setCompanyPage
    ] = useState<number>(1);

    const [
        hasMoreCompanies,
        setHasMoreCompanies
    ] = useState<boolean>(true);

    const [
        loadingCompanies,
        setLoadingCompanies
    ] = useState<boolean>(false);

    const [
        selectedCompanies,
        setSelectedCompanies
    ] = useState<CompanySelectionType[]>([]);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        if (isVisible) {
            setFormData({
                homeAddress: memberData?.metadata?.homeAddress as UserHomeAddressRequest | undefined,
                education: (memberData?.metadata?.education as UserEducationRequest[]) || [],
                maritalStatus: memberData?.metadata?.maritalStatus || "",
                aboutMyTrade: memberData?.user?.aboutMyTrade || "",
                birthPlace: memberData?.metadata?.birthPlace || "",
                fatherName: memberData?.metadata?.fatherName || "",
                motherName: memberData?.metadata?.motherName || "",
                birthDate: memberData?.metadata?.birthDate || "",
                bloodType: memberData?.metadata?.bloodType || "",
                phoneNumber: memberData?.user?.phoneNumber || "",
                isStudent: memberData?.user?.isStudent || false,
                memberCode: memberData?.user?.memberCode || "",
                firstName: memberData?.user?.firstName || "",
                gender: memberData?.metadata?.gender || "",
                lastName: memberData?.user?.lastName || "",
                tcNo: memberData?.metadata?.tcNo || "",
                mail: memberData?.user?.mail || "",
                existingCompanies: [],
                newCompanies: []
            });
            if (memberData?.user?.profilePhotoPath) {
                setLogoPreview(memberData.user.profilePhotoPath);
            } else {
                setLogoPreview("");
            }
            setSelectedCompanies(memberData?.companies?.map(c => ({
                positionID: (c as unknown as Record<string, unknown>)?.positionID as string || (c as unknown as Record<string, { id: string }>)?.position?.id || "",
                title: c.title || "",
                _id: c._id || "",
                isNew: false
            })) || []);
            setCompanyPage(1);
            setCompanies([]);
            setHasMoreCompanies(true);
            setCompanySearchText("");
            fetchPositions();
            fetchCities();
            fetchUniversities();
            setIsFormReady(true);
        } else {
            setIsFormReady(false);
            setFormData({
                existingCompanies: [],
                isStudent: false,
                newCompanies: [],
                education: []
            });
            setSelectedCompanies([]);
            setLogoPreview("");
            setLogoFile(null);
        }
    }, [
        isVisible,
        memberData
    ]);

    const fetchPositions = async () => {
        try {
            const result = await RESTServiceContext.action("getPositions", null);
            if (result.payload) {
                setPositions(result.payload);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCompanies = useCallback(async (page: number, isReset: boolean = false) => {
        try {
            setLoadingCompanies(true);
            const requestParams: Record<string, unknown> = {
                page
            };
            if (companySearchText) {
                requestParams.searchText = companySearchText;
            }

            const result = await RESTServiceContext.action("getAllCompaniesForAdmin", requestParams);
            if (result.payload) {
                if (result.payload.length < 20) {
                    setHasMoreCompanies(false);
                } else {
                    setHasMoreCompanies(true);
                }

                if (isReset) {
                    setCompanies(result.payload);
                } else {
                    setCompanies(prev => [
                        ...prev,
                        ...result.payload!
                    ]);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingCompanies(false);
        }
    }, [companySearchText]);

    useEffect(() => {
        if (isCompanySheetVisible) {
            fetchCompanies(companyPage, companyPage === 1);
        }
    }, [
        fetchCompanies,
        companyPage,
        isCompanySheetVisible
    ]);

    const handleSearchCompany = (text: string) => {
        setCompanySearchText(text);
        setCompanyPage(1);
        setHasMoreCompanies(true);
    };

    const updateField = (field: keyof RegisterUserRequestType, value: unknown) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const fetchCities = async () => {
        try {
            const result = await RESTServiceContext.action("getCities", null);
            if (result.payload) setCities(result.payload);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchUniversities = async () => {
        try {
            const result = await RESTServiceContext.action("getUniversities", null);
            if (result.payload) setUniversities(result.payload);
        } catch (error) {
            console.error(error);
        }
    };

    const updateHomeAddress = (field: keyof UserHomeAddressRequest, value: unknown) => {
        setFormData(prev => ({
            ...prev,
            homeAddress: {
                ...(prev.homeAddress || {
                    addressDetail: "",
                    neighborhood: "",
                    districtID: "",
                    cityID: ""
                }),
                [field]: value
            } as never
        }));
    };

    useEffect(() => {
        if (formData.homeAddress?.cityID) {
            setDistricts([]);
            RESTServiceContext.action("getDistricts", {
                cityID: formData.homeAddress.cityID
            }).then(result => {
                if (result.payload) setDistricts(result.payload);
            });
        }
    }, [formData.homeAddress?.cityID]);

    useEffect(() => {
        if (newEducationDraft?.universityID) {
            setFaculties([]);
            setDepartments([]);
            RESTServiceContext.action("getFaculties", {
                universityID: newEducationDraft.universityID
            }).then(result => {
                if (result.payload) setFaculties(result.payload);
            });
        }
    }, [newEducationDraft?.universityID]);

    useEffect(() => {
        if (newEducationDraft?.facultyID) {
            setDepartments([]);
            RESTServiceContext.action("getDepartments", {
                facultyID: newEducationDraft.facultyID
            }).then(result => {
                if (result.payload) setDepartments(result.payload);
            });
        }
    }, [newEducationDraft?.facultyID]);

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        try {
            setIsSubmitting(true);
            const existingCompanies = selectedCompanies.filter(c => !c.isNew).map(c => ({
                positionID: c.positionID || "",
                companyID: c._id
            }));
            const changedFields: Record<string, unknown> = {};

            if (formData.aboutMyTrade !== (memberData?.user?.aboutMyTrade || "")) {
                changedFields.aboutMyTrade = formData.aboutMyTrade;
            }

            if (formData.maritalStatus !== (memberData?.metadata?.maritalStatus || "") && formData.maritalStatus) {
                changedFields.maritalStatus = formData.maritalStatus;
            }

            if (formData.phoneNumber !== (memberData?.user?.phoneNumber || "")) {
                changedFields.phoneNumber = formData.phoneNumber;
            }

            if (formData.fatherName !== (memberData?.metadata?.fatherName || "") && formData.fatherName) {
                changedFields.fatherName = formData.fatherName;
            }

            if (formData.motherName !== (memberData?.metadata?.motherName || "") && formData.motherName) {
                changedFields.motherName = formData.motherName;
            }

            if (formData.birthPlace !== (memberData?.metadata?.birthPlace || "") && formData.birthPlace) {
                changedFields.birthPlace = formData.birthPlace;
            }

            if (formData.memberCode !== (memberData?.user?.memberCode || "")) {
                changedFields.memberCode = formData.memberCode;
            }

            if (formData.birthDate !== (memberData?.metadata?.birthDate || "") && formData.birthDate) {
                changedFields.birthDate = formData.birthDate;
            }

            if (formData.bloodType !== (memberData?.metadata?.bloodType || "") && formData.bloodType) {
                changedFields.bloodType = formData.bloodType;
            }

            if (formData.firstName !== (memberData?.user?.firstName || "")) {
                changedFields.firstName = formData.firstName;
            }

            if (Boolean(formData.isStudent) !== Boolean(memberData?.user?.isStudent)) {
                changedFields.isStudent = formData.isStudent;
            }

            if (formData.lastName !== (memberData?.user?.lastName || "")) {
                changedFields.lastName = formData.lastName;
            }

            if (formData.gender !== (memberData?.metadata?.gender || "") && formData.gender) {
                changedFields.gender = formData.gender;
            }

            if (formData.tcNo !== (memberData?.metadata?.tcNo || "") && formData.tcNo) {
                changedFields.tcNo = formData.tcNo;
            }

            if (formData.mail !== (memberData?.user?.mail || "")) {
                changedFields.mail = formData.mail;
            }

            const hasValidAddress = Boolean(formData.homeAddress?.cityID && formData.homeAddress?.districtID);
            if (hasValidAddress && JSON.stringify(formData.homeAddress) !== JSON.stringify(memberData?.metadata?.homeAddress)) {
                changedFields.homeAddress = formData.homeAddress;
            }

            const origEducation = memberData?.metadata?.education || [];
            if (formData.education && formData.education.length > 0 && JSON.stringify(formData.education) !== JSON.stringify(origEducation)) {
                changedFields.education = formData.education;
            }

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            const originalCompanies = memberData?.companies?.map((c: Record<string, unknown>) => ({
                positionID: (c?.positionID as string) || (c?.position as { id?: string })?.id || "",
                companyID: c._id
            })) || [];

            if (JSON.stringify(existingCompanies) !== JSON.stringify(originalCompanies)) {
                changedFields.existingCompanies = existingCompanies;
            }

            const newUserID = memberData?.user?._id;

            const uploadPhoto = async (targetUserID: string, file: File) => {
                try {
                    const res = await RESTServiceContext.action("getUploadTokenForUserProfilePhoto", {
                        userID: targetUserID
                    });
                    const token = res.payload?.uploadToken || res.payload?.token;
                    if (token) {
                        const formDataToUpload = new FormData();
                        formDataToUpload.append("file", file);
                        const uploadResponse = await fetch(`${process.env.STORAGE_URL}/upload`, {
                            headers: {
                                Authorization: token
                            },
                            body: formDataToUpload,
                            method: "POST"
                        });
                        if (!uploadResponse.ok) {
                            console.error("Storage upload failed:", uploadResponse.status);
                        }
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    } else {
                        console.error("No upload token received from server:", res);
                    }
                } catch (error) {
                    console.error("Photo upload failed:", error);
                }
            };

            if (Object.keys(changedFields).length === 0) {
                if (newUserID && logoFile) {
                    await uploadPhoto(newUserID, logoFile);
                    NCoreUIKitSnackBar.open({
                        title: localize("members-form-success") || "Üye başarıyla güncellendi."
                    });
                    if (onSuccess) onSuccess();
                    onClose();
                } else {
                    NCoreUIKitSnackBar.open({
                        title: localize("members-no-changes") || "Değişiklik yapılmadı."
                    });
                }
                setIsSubmitting(false);
                return;
            }

            const payload: Record<string, unknown> = {
                ...changedFields,
                userID: newUserID
            };

            const result = await RESTServiceContext.action("updateUser", payload);
            if (result.code === 200 || result.payload) {
                if (newUserID && logoFile) {
                    await uploadPhoto(newUserID, logoFile);
                }

                NCoreUIKitSnackBar.open({
                    title: localize("members-form-success") || "Üye başarıyla güncellendi."
                });
                if (onSuccess) {
                    onSuccess();
                }
                onClose();
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("members-error-save") || "Üye güncellenirken hata oluştu."
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("members-error-save") || "Üye eklenirken hata oluştu."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const isFormChanged = useMemo(() => {
        if (logoFile) {
            return true;
        }

        if (formData.aboutMyTrade !== (memberData?.user?.aboutMyTrade || "")) {
            return true;
        }

        if (formData.maritalStatus !== (memberData?.metadata?.maritalStatus || "") && formData.maritalStatus) {
            return true;
        }

        if (formData.phoneNumber !== (memberData?.user?.phoneNumber || "")) {
            return true;
        }

        if (formData.fatherName !== (memberData?.metadata?.fatherName || "") && formData.fatherName) {
            return true;
        }

        if (formData.motherName !== (memberData?.metadata?.motherName || "") && formData.motherName) {
            return true;
        }

        if (formData.birthPlace !== (memberData?.metadata?.birthPlace || "") && formData.birthPlace) {
            return true;
        }

        if (formData.memberCode !== (memberData?.user?.memberCode || "")) {
            return true;
        }

        if (formData.birthDate !== (memberData?.metadata?.birthDate || "") && formData.birthDate) {
            return true;
        }

        if (formData.bloodType !== (memberData?.metadata?.bloodType || "") && formData.bloodType) {
            return true;
        }

        if (formData.firstName !== (memberData?.user?.firstName || "")) {
            return true;
        }

        if (Boolean(formData.isStudent) !== Boolean(memberData?.user?.isStudent)) {
            return true;
        }

        if (formData.lastName !== (memberData?.user?.lastName || "")) {
            return true;
        }

        if (formData.gender !== (memberData?.metadata?.gender || "") && formData.gender) {
            return true;
        }

        if (formData.tcNo !== (memberData?.metadata?.tcNo || "") && formData.tcNo) {
            return true;
        }

        if (formData.mail !== (memberData?.user?.mail || "")) {
            return true;
        }

        const hasValidAddress = Boolean(formData.homeAddress?.cityID && formData.homeAddress?.districtID);
        if (hasValidAddress && JSON.stringify(formData.homeAddress) !== JSON.stringify(memberData?.metadata?.homeAddress)) {
            return true;
        }

        const origEducation = memberData?.metadata?.education || [];
        if (formData.education && formData.education.length > 0 && JSON.stringify(formData.education) !== JSON.stringify(origEducation)) {
            return true;
        }

        const existingCompanies = selectedCompanies.filter(c => !c.isNew).map(c => ({
            positionID: c.positionID || "",
            companyID: c._id
        }));

        const originalCompanies = memberData?.companies?.map((c: Record<string, unknown>) => ({
            positionID: (c?.positionID as string) || (c?.position as { id?: string })?.id || "",
            companyID: c._id
        })) || [];

        if (JSON.stringify(existingCompanies) !== JSON.stringify(originalCompanies)) {
            return true;
        }

        return false;
    }, [
        selectedCompanies,
        memberData,
        formData,
        logoFile
    ]);

    const isFormValid = useMemo(() => {
        if (formData.firstName && formData.firstName.trim().length < 2) {
            return false;
        }

        if (formData.lastName && formData.lastName.trim().length < 2) {
            return false;
        }

        if (formData.memberCode && formData.memberCode.trim().length < 2) {
            return false;
        }

        if (formData.mail && !emailValidation(formData.mail)) {
            return false;
        }

        if (formData.phoneNumber && !phoneValidation(formData.phoneNumber)) {
            return false;
        }

        if (formData.tcNo && formData.tcNo !== (memberData?.metadata?.tcNo || "") && !tckValidation(formData.tcNo)) {
            return false;
        }

        if (formData.homeAddress?.cityID || formData.homeAddress?.districtID || formData.homeAddress?.neighborhood) {
            if (!formData.homeAddress.cityID || !formData.homeAddress.districtID || !formData.homeAddress.neighborhood) {
                return false;
            }
        }

        if (formData.education && formData.education.length > 0) {
            for (const edu of formData.education) {
                if (!edu.universityID || !edu.facultyID || !edu.departmentID || !edu.graduationYear) {
                    return false;
                }
            }
        }

        return true;
    }, [
        memberData,
        formData
    ]);

    const handleSubmit = () => {
        if (formData.mail && !emailValidation(formData.mail)) {
            NCoreUIKitSnackBar.open({
                title: localize("members-invalid-email") || "Geçersiz e-posta formatı."
            });
            return;
        }

        if (formData.phoneNumber && !phoneValidation(formData.phoneNumber)) {
            NCoreUIKitSnackBar.open({
                title: localize("members-invalid-phone") || "Geçersiz telefon formatı."
            });
            return;
        }

        if (formData.tcNo && formData.tcNo !== (memberData?.metadata?.tcNo || "") && !tckValidation(formData.tcNo)) {
            NCoreUIKitSnackBar.open({
                title: localize("members-invalid-tckn") || "Geçersiz TCKN formatı."
            });
            return;
        }

        setIsDialogVisible(true);
    };

    return <Fragment>
        <Modal
            portalName="modal-system"
            id="member-edit-modal"
            isActive={isVisible}
            onClosed={onClose}
        >
            {
                isFormReady ?
                    <ScrollView
                        style={stylesheet.container}
                    >
                        <View
                            style={{
                                padding: isMobile ? spaces.spacingMd : spaces.spacingXl,
                                gap: isMobile ? spaces.spacingMd : spaces.spacingXl,
                                backgroundColor: colors.project.brandWhite,
                                borderRadius: radiuses.lg
                            }}
                        >
                            <View
                                style={{
                                    justifyContent: "space-between",
                                    flexDirection: "row",
                                    alignItems: "center"
                                }}
                            >
                                <Text
                                    variant="titleLargeSize"
                                >
                                    {localize("members-edit") || "Üye Düzenle"}
                                </Text>
                                <TouchableOpacity
                                    onPress={onClose}
                                >
                                    <XIcon
                                        color={colors.content.icon.mid}
                                        size={24}
                                    />
                                </TouchableOpacity>
                            </View>
                            {/* IMAGES */}
                            <View
                                style={{
                                    alignItems: "center",
                                    gap: spaces.spacingMd
                                }}
                            >
                                <TouchableOpacity
                                    onPress={() => logoInputRef.current?.click()}
                                >
                                    {
                                        logoPreview ?
                                            <Image
                                                style={[
                                                    stylesheet.logoPreview,
                                                    {
                                                        borderRadius: radiuses.full
                                                    }
                                                ]}
                                                source={{
                                                    uri: logoPreview
                                                }}
                                            />
                                        :
                                            <View
                                                style={[
                                                    stylesheet.logoPlaceholder,
                                                    {
                                                        backgroundColor: colors.project.brandBorder,
                                                        borderRadius: radiuses.full
                                                    }
                                                ]}
                                            >
                                                <PlusIcon
                                                    color={colors.content.icon.mid}
                                                    size={24}
                                                />
                                            </View>
                                    }
                                </TouchableOpacity>
                                <Text
                                    customColor="content.text.mid"
                                    variant="bodySmallSize"
                                >
                                    {localize("members-form-profile-photo") || "Profil Fotoğrafı"}
                                </Text>
                                <input
                                    onChange={handleLogoSelect}
                                    style={{
                                        display: "none"
                                    }}
                                    ref={logoInputRef}
                                    accept="image/*"
                                    type="file"
                                />
                            </View>
                            <Seperator
                                color="subtle"
                            />
                            {/* BASIC INFO */}
                            <View
                                style={[
                                    stylesheet.formContainer,
                                    {
                                        gap: spaces.spacingLg
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {localize("members-form-basic-info")}
                                </Text>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        type={(formData.firstName || "").length > 0 && (formData.firstName || "").trim().length < 2 ? "danger" : "default"}
                                        onChangeText={(text) => updateField("firstName", text)}
                                        title={localize("members-form-firstname")}
                                        value={formData.firstName || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <TextInput
                                        type={(formData.lastName || "").length > 0 && (formData.lastName || "").trim().length < 2 ? "danger" : "default"}
                                        onChangeText={(text) => updateField("lastName", text)}
                                        title={localize("members-form-lastname")}
                                        value={formData.lastName || ""}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        type={(formData.tcNo || "").length > 0 && !tckValidation(formData.tcNo || "") ? "danger" : "default"}
                                        onChangeText={(text) => updateField("tcNo", text)}
                                        title={localize("members-form-tcno")}
                                        value={formData.tcNo || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <TextInput
                                        type={(formData.memberCode || "").length > 0 && (formData.memberCode || "").trim().length < 2 ? "danger" : "default"}
                                        onChangeText={(text) => updateField("memberCode", text)}
                                        title={localize("members-form-membercode")}
                                        value={formData.memberCode || ""}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        type={(formData.mail || "").length > 0 && !emailValidation(formData.mail || "") ? "danger" : "default"}
                                        onChangeText={(text) => updateField("mail", text)}
                                        title={localize("members-form-mail")}
                                        value={formData.mail || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <TextInput
                                        type={(formData.phoneNumber || "").length > 0 && !phoneValidation(formData.phoneNumber || "") ? "danger" : "default"}
                                        onChangeText={(text) => updateField("phoneNumber", text)}
                                        title={localize("members-form-phone")}
                                        value={formData.phoneNumber || ""}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        title={localize("members-form-birthplace") || "Doğum Yeri"}
                                        onChangeText={(text) => updateField("birthPlace", text)}
                                        value={formData.birthPlace || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <DateTimePicker
                                        onChange={(val) => {
                                            if (val.date) {
                                                updateField("birthDate", typeof val.date === "string" ? val.date : val.date.toISOString());
                                            } else {
                                                updateField("birthDate", "");
                                            }
                                        }}
                                        initialDate={formData.birthDate ? new Date(formData.birthDate) : undefined}
                                        title={localize("members-form-birthdate")}
                                        dateTimeSheetProps={{
                                            portalName: "modal-system"
                                        }}
                                        isWorkWithRealtime={false}
                                        spreadBehaviour="stretch"
                                        titleFormat="DD.MM.YYYY"
                                        placeholder="gg.aa.yyyy"
                                        pickerType="date"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        title={localize("members-form-fathername") || "Baba Adı"}
                                        onChangeText={(text) => updateField("fatherName", text)}
                                        value={formData.fatherName || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <TextInput
                                        title={localize("members-form-mothername") || "Anne Adı"}
                                        onChangeText={(text) => updateField("motherName", text)}
                                        value={formData.motherName || ""}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        title={localize("members-form-maritalstatus") || "Medeni Durum"}
                                        onChangeText={(text) => updateField("maritalStatus", text)}
                                        value={formData.maritalStatus || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <TextInput
                                        title={localize("members-form-gender") || "Cinsiyet"}
                                        onChangeText={(text) => updateField("gender", text)}
                                        value={formData.gender || ""}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        title={localize("members-form-bloodtype") || "Kan Grubu"}
                                        onChangeText={(text) => updateField("bloodType", text)}
                                        value={formData.bloodType || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <TextInput
                                        title={localize("members-form-aboutmytrade") || "Mesleğim Hakkında"}
                                        onChangeText={(text) => updateField("aboutMyTrade", text)}
                                        value={formData.aboutMyTrade || ""}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <View
                                        style={[
                                            stylesheet.row,
                                            {
                                                gap: spaces.spacingMd,
                                                flex: 1
                                            }
                                        ]}
                                    >
                                        <Text
                                            variant="bodyMediumSize"
                                        >
                                            {localize("members-form-isstudent")}
                                        </Text>
                                        <Switch
                                            onPress={() => updateField("isStudent", !formData.isStudent)}
                                            isActive={formData.isStudent || false}
                                            type="neutral"
                                        />
                                    </View>
                                </View>
                            </View>
                            <Seperator
                                color="subtle"
                            />
                            {/* HOME ADDRESS */}
                            <View
                                style={[
                                    stylesheet.formContainer,
                                    {
                                        gap: spaces.spacingLg
                                    }
                                ]}
                            >
                                <View
                                    style={stylesheet.headerContainer}
                                >
                                    <Text
                                        variant="titleMediumSize"
                                    >
                                        {localize("members-form-address") || "Ev Adresi"}
                                    </Text>
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <SelectBox
                                        initialSelectedItems={formData.homeAddress?.cityID ? [
                                            {
                                                __title: cities?.find(c => c._id === formData.homeAddress?.cityID)?.value || "",
                                                __key: formData.homeAddress.cityID
                                            }
                                        ] : []}
                                        onChange={selectedItems => {
                                            const item = selectedItems[0];
                                            if (item) {
                                                updateHomeAddress("cityID", item.__key);
                                            } else {
                                                updateHomeAddress("cityID", "");
                                            }
                                            updateHomeAddress("districtID", "");
                                        }}
                                        title={localize("members-form-city") || "İl"}
                                        key={`city-select-${cities?.length || 0}`}
                                        titleExtractor={item => item.title}
                                        keyExtractor={item => item.value}
                                        data={(cities || []).map(c => ({
                                            title: c.value,
                                            value: c._id
                                        }))}
                                        selectSheetProps={{
                                            portalName: "modal-system"
                                        }}
                                        spreadBehaviour="stretch"
                                    />
                                    <SelectBox
                                        initialSelectedItems={formData.homeAddress?.districtID ? [
                                            {
                                                __title: districts?.find(d => d._id === formData.homeAddress?.districtID)?.value || "",
                                                __key: formData.homeAddress.districtID
                                            }
                                        ] : []}
                                        onChange={selectedItems => {
                                            const item = selectedItems[0];
                                            if (item) {
                                                updateHomeAddress("districtID", item.__key);
                                            } else {
                                                updateHomeAddress("districtID", "");
                                            }
                                        }}
                                        title={localize("members-form-district") || "İlçe"}
                                        key={`district-select-${districts?.length || 0}`}
                                        isDisabled={!formData.homeAddress?.cityID}
                                        titleExtractor={item => item.title}
                                        data={(districts || []).map(d => ({
                                            title: d.value,
                                            value: d._id
                                        }))}
                                        keyExtractor={item => item.value}
                                        selectSheetProps={{
                                            portalName: "modal-system"
                                        }}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                                <View
                                    style={isMobile ? {
                                        flexDirection: "column",
                                        gap: spaces.spacingMd
                                    } : [
                                        stylesheet.row,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <TextInput
                                        onChangeText={(text) => updateHomeAddress("neighborhood", text)}
                                        title={localize("members-form-neighborhood") || "Mahalle"}
                                        value={formData.homeAddress?.neighborhood || ""}
                                        spreadBehaviour="stretch"
                                    />
                                    <TextInput
                                        onChangeText={(text) => updateHomeAddress("addressDetail", text)}
                                        title={localize("members-form-addressdetail") || "Adres Detayı"}
                                        value={formData.homeAddress?.addressDetail || ""}
                                        spreadBehaviour="stretch"
                                    />
                                </View>
                            </View>
                            <Seperator
                                color="subtle"
                            />
                            {/* COMPANY */}
                            <View
                                style={[
                                    stylesheet.formContainer,
                                    {
                                        gap: spaces.spacingLg
                                    }
                                ]}
                            >
                                <View
                                    style={stylesheet.headerContainer}
                                >
                                    <Text
                                        variant="titleMediumSize"
                                    >
                                        {localize("members-form-companies") || "Şirketler"}
                                    </Text>
                                    <Button
                                        title={localize("members-add-company") || "Şirket Ekle"}
                                        onPress={() => setIsCompanySheetVisible(true)}
                                        icon={({
                                            size
                                        }) => <PlusIcon
                                            color={colors.project.brandWhite}
                                            size={size}
                                        />}
                                        customBorderColor="secondaryButton"
                                        customColor="secondaryButton"
                                        spreadBehaviour="free"
                                        variant="filled"
                                        size="small"
                                    />
                                </View>
                                <View
                                    style={{
                                        gap: spaces.spacingMd
                                    }}
                                >
                                    {
                                        selectedCompanies.map((comp, index) => {
                                            return <View
                                                style={[
                                                    stylesheet.companyCard,
                                                    {
                                                        borderColor: colors.project.brandBorder,
                                                        borderWidth: borders.seperator,
                                                        padding: spaces.spacingMd,
                                                        borderRadius: radiuses.md
                                                    }
                                                ]}
                                                key={`comp-${index}`}
                                            >
                                                <View
                                                    style={[
                                                        stylesheet.row,
                                                        {
                                                            gap: spaces.spacingMd
                                                        }
                                                    ]}
                                                >
                                                    <Text
                                                        variant="bodyMediumSize"
                                                    >
                                                        {comp.title}
                                                    </Text>
                                                </View>
                                                <View
                                                    style={stylesheet.positionSelectContainer}
                                                >
                                                    <SelectBox
                                                        initialSelectedItems={comp.positionID ? [
                                                            {
                                                                __title: positions.find(p => p._id === comp.positionID)?.value || "",
                                                                __key: comp.positionID
                                                            }
                                                        ] : []}
                                                        placeholder={localize("members-form-position") || "Pozisyon Seçiniz"}
                                                        key={`position-select-${positions?.length || 0}-${comp._id}`}
                                                        onChange={selectedItems => {
                                                            const item = selectedItems[0];
                                                            setSelectedCompanies(prev => {
                                                                const newComps = [
                                                                    ...prev
                                                                ];
                                                                const c = newComps[index];
                                                                if (c) {
                                                                    if (item) {
                                                                        c.positionID = item.__key;
                                                                    } else {
                                                                        c.positionID = undefined;
                                                                    }
                                                                }
                                                                return newComps;
                                                            });
                                                        }}
                                                        titleExtractor={item => item.title}
                                                        keyExtractor={item => item.value}
                                                        selectSheetProps={{
                                                            portalName: "modal-system"
                                                        }}
                                                        data={positions.map(p => ({
                                                            title: p.value,
                                                            value: p._id
                                                        }))}
                                                        spreadBehaviour="stretch"
                                                    />
                                                </View>
                                                <TouchableOpacity
                                                    onPress={() => {
                                                        setSelectedCompanies(prev => prev.filter((_, i) => i !== index));
                                                    }}
                                                >
                                                    <XIcon
                                                        color={colors.content.icon.danger}
                                                        size={20}
                                                    />
                                                </TouchableOpacity>
                                            </View>;
                                        })
                                    }
                                </View>
                            </View>
                            <Seperator
                                color="subtle"
                            />
                            {/* EDUCATION */}
                            <View
                                style={[
                                    stylesheet.formContainer,
                                    {
                                        gap: spaces.spacingLg
                                    }
                                ]}
                            >
                                <View
                                    style={[
                                        stylesheet.row,
                                        {
                                            justifyContent: "space-between",
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <Text
                                        variant="titleMediumSize"
                                    >
                                        {localize("members-form-education") || "Eğitim Durumu"}
                                    </Text>
                                    <Button
                                        title={localize("members-add-education") || "Eğitim Ekle"}
                                        onPress={() => setIsEducationSheetVisible(true)}
                                        icon={({
                                            size
                                        }) => <PlusIcon
                                            color={colors.project.brandWhite}
                                            size={size}
                                        />}
                                        customBorderColor="secondaryButton"
                                        customColor="secondaryButton"
                                        spreadBehaviour="free"
                                        variant="filled"
                                        size="small"
                                    />
                                </View>
                                <View
                                    style={{
                                        gap: spaces.spacingMd
                                    }}
                                >
                                    {
                                        (formData.education || []).map((edu, index) => {
                                            return <View
                                                style={[
                                                    stylesheet.companyCard,
                                                    {
                                                        borderColor: colors.project.brandBorder,
                                                        borderWidth: borders.seperator,
                                                        padding: spaces.spacingMd,
                                                        borderRadius: radiuses.md
                                                    }
                                                ]}
                                                key={`edu-${index}`}
                                            >
                                                <View
                                                    style={[
                                                        stylesheet.row,
                                                        {
                                                            gap: spaces.spacingMd
                                                        }
                                                    ]}
                                                >
                                                    <Text
                                                        variant="bodyMediumSize"
                                                    >
                                                        {universities?.find(u => u._id === edu.universityID)?.value || edu.universityID} {edu.graduationYear ? `- ${edu.graduationYear}` : ""}
                                                    </Text>
                                                </View>
                                                <View
                                                    style={[
                                                        stylesheet.row,
                                                        {
                                                            right: spaces.spacingMd,
                                                            top: spaces.spacingMd,
                                                            position: "absolute",
                                                            gap: spaces.spacingSm
                                                        }
                                                    ]}
                                                >
                                                    <TouchableOpacity
                                                        onPress={() => {
                                                            setEditEducationIndex(index);
                                                            setNewEducationDraft(edu);
                                                            setIsEducationSheetVisible(true);
                                                        }}
                                                    >
                                                        <PencilIcon
                                                            color={colors.content.icon.mid}
                                                            size={20}
                                                        />
                                                    </TouchableOpacity>
                                                    <TouchableOpacity
                                                        onPress={() => {
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                education: prev.education?.filter((_, i) => i !== index)
                                                            }));
                                                        }}
                                                    >
                                                        <Trash2Icon
                                                            color={colors.content.icon.mid}
                                                            size={20}
                                                        />
                                                    </TouchableOpacity>
                                                </View>
                                            </View>;
                                        })
                                    }
                                </View>
                            </View>
                            <View
                                style={[
                                    stylesheet.footerContainer,
                                    {
                                        flexDirection: isMobile ? "column-reverse" : "row",
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Button
                                    title={localize("members-form-cancel") || "İptal"}
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    customBorderColor="secondaryButton"
                                    customColor="secondaryButton"
                                    onPress={onClose}
                                    variant="filled"
                                />
                                <Button
                                    icon={({
                                        color,
                                        size
                                    }) => <SaveIcon
                                        color={colors.project.brandWhite || colors.content.icon[color]}
                                        size={size}
                                    />}
                                    isDisabled={!isFormChanged || !isFormValid || isSubmitting}
                                    title={localize("members-form-save") || "Kaydet"}
                                    spreadBehaviour={isMobile ? "stretch" : "free"}
                                    customTextColor={colors.project.brandWhite}
                                    customBorderColor="gold"
                                    isLoading={isSubmitting}
                                    onPress={handleSubmit}
                                    customColor="gold"
                                    variant="filled"
                                />
                            </View>
                        </View>
                    </ScrollView>
                :
                    <View
                        style={[
                            stylesheet.loadingContainer,
                            {
                                padding: spaces.spacingXl
                            }
                        ]}
                    >
                        <ActivityIndicator
                            color={colors.project.gold}
                            size="large"
                        />
                    </View>
            }
        </Modal>
        <Dialog
            primaryButtonProps={{
                title: localize("members-form-dialog-yes") || "Evet, Kaydet",
                onPress: handleConfirmSave
            }}
            secondaryButtonProps={{
                title: localize("members-form-dialog-no") || "Vazgeç",
                onPress: () => setIsDialogVisible(false)
            }}
            title={localize("members-edit") || "Üye Düzenle"}
            onOverlayPress={() => setIsDialogVisible(false)}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg,
                    gap: spaces.spacingSm
                }}
            >
                <Text
                    customColor="content.text.high"
                    variant="bodyLargeSize"
                >
                    Yapılan değişiklikleri kaydetmek istediğinize emin misiniz?
                </Text>
                <Text
                    customColor="content.text.mid"
                    variant="bodyMediumSize"
                >
                    Profil fotoğrafı ve güncellenen üye bilgileri sisteme işlenecektir.
                </Text>
            </View>
        </Dialog>
        <BottomSheet
            onClose={() => setIsCompanySheetVisible(false)}
            isActive={isCompanySheetVisible}
            portalName="modal-system"
        >
            <View
                style={[
                    stylesheet.sheetContent,
                    {
                        backgroundColor: colors.project.brandWhite,
                        padding: spaces.spacingXl,
                        borderRadius: radiuses.lg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("members-select-company") || "Firma Seç veya Ekle"}
                </Text>
                <TextInput
                    placeholder={localize("search-company") || "Firma Ara veya Yeni Ekle"}
                    onChangeText={handleSearchCompany}
                    value={companySearchText}
                    spreadBehaviour="stretch"
                />
                <FlatList
                    renderItem={({
                        item
                    }) => <TouchableOpacity
                        onPress={() => {
                            if (!selectedCompanies.find(c => c._id === item._id)) {
                                setSelectedCompanies(prev => [
                                    ...prev,
                                    {
                                        ...item,
                                        isNew: false
                                    }
                                ]);
                            }
                            setIsCompanySheetVisible(false);
                        }}
                        style={{
                            borderBottomColor: colors.project.brandBorder,
                            borderBottomWidth: borders.seperator,
                            paddingVertical: spaces.spacingMd
                        }}
                    >
                        <Text
                            variant="bodyMediumSize"
                        >
                            {item.title}
                        </Text>
                    </TouchableOpacity>}
                    onEndReached={() => {
                        if (hasMoreCompanies && !loadingCompanies) {
                            setCompanyPage(prev => prev + 1);
                        }
                    }}
                    ListFooterComponent={
                        loadingCompanies ?
                            <ActivityIndicator
                                style={{
                                    margin: spaces.spacingMd
                                }}
                                color={colors.project.gold}
                            />
                        :
                            null
                    }
                    keyExtractor={item => item._id}
                    onEndReachedThreshold={0.5}
                    data={companies}
                />
            </View>
        </BottomSheet>
        <Modal
            onClosed={() => {
                setIsEducationSheetVisible(false);
                setEditEducationIndex(null);
                setNewEducationDraft(null);
            }}
            isActive={isEducationSheetVisible}
            portalName="modal-system"
            id="new-education-modal"
        >
            <ScrollView
                style={stylesheet.container}
            >
                <View
                    style={{
                        backgroundColor: colors.project.brandWhite,
                        padding: spaces.spacingXl,
                        gap: spaces.spacingXl
                    }}
                >
                    <View
                        style={{
                            justifyContent: "space-between",
                            flexDirection: "row",
                            alignItems: "center"
                        }}
                    >
                        <Text
                            variant="titleLargeSize"
                        >
                            {localize("members-form-education") || "Eğitim Ekle"}
                        </Text>
                        <TouchableOpacity
                            onPress={() => setIsEducationSheetVisible(false)}
                        >
                            <XIcon
                                color={colors.content.icon.mid}
                                size={24}
                            />
                        </TouchableOpacity>
                    </View>
                    <View
                        key={editEducationIndex !== null ? editEducationIndex : "new"}
                        style={{
                            gap: spaces.spacingMd
                        }}
                    >
                        <SelectBox
                            initialSelectedItems={newEducationDraft?.universityID ? [
                                {
                                    __title: universities?.find(u => u._id === newEducationDraft.universityID)?.value || "",
                                    __key: newEducationDraft.universityID
                                }
                            ] : []}
                            title={localize("members-form-university") || "Üniversite"}
                            key={`university-select-${universities?.length || 0}`}
                            onChange={selectedItems => {
                                const item = selectedItems[0];
                                setNewEducationDraft(prev => ({
                                    ...(prev || {
                                        graduationYear: 0,
                                        departmentID: "",
                                        universityID: "",
                                        facultyID: ""
                                    }),
                                    universityID: item ? item.__key : "",
                                    departmentID: "",
                                    facultyID: ""
                                }));
                            }}
                            data={(universities || []).map(u => ({
                                title: u.value,
                                value: u._id
                            }))}
                            titleExtractor={item => item.title}
                            keyExtractor={item => item.value}
                            selectSheetProps={{
                                portalName: "modal-system"
                            }}
                            spreadBehaviour="stretch"
                        />
                        <SelectBox
                            initialSelectedItems={newEducationDraft?.facultyID ? [
                                {
                                    __title: faculties?.find(f => f._id === newEducationDraft.facultyID)?.value || "",
                                    __key: newEducationDraft.facultyID
                                }
                            ] : []}
                            title={localize("members-form-faculty") || "Fakülte"}
                            key={`faculty-select-${faculties?.length || 0}`}
                            isDisabled={!newEducationDraft?.universityID}
                            onChange={selectedItems => {
                                const item = selectedItems[0];
                                setNewEducationDraft(prev => ({
                                    ...(prev || {
                                        graduationYear: 0,
                                        departmentID: "",
                                        universityID: "",
                                        facultyID: ""
                                    }),
                                    facultyID: item ? item.__key : "",
                                    departmentID: ""
                                }));
                            }}
                            titleExtractor={item => item.title}
                            data={(faculties || []).map(f => ({
                                title: f.value,
                                value: f._id
                            }))}
                            keyExtractor={item => item.value}
                            selectSheetProps={{
                                portalName: "modal-system"
                            }}
                            spreadBehaviour="stretch"
                        />
                        <SelectBox
                            initialSelectedItems={newEducationDraft?.departmentID ? [
                                {
                                    __title: departments?.find(d => d._id === newEducationDraft.departmentID)?.value || "",
                                    __key: newEducationDraft.departmentID
                                }
                            ] : []}
                            title={localize("members-form-department") || "Bölüm"}
                            key={`department-select-${departments?.length || 0}`}
                            onChange={selectedItems => {
                                const item = selectedItems[0];
                                setNewEducationDraft(prev => ({
                                    ...(prev || {
                                        graduationYear: 0,
                                        departmentID: "",
                                        universityID: "",
                                        facultyID: ""
                                    }),
                                    departmentID: item ? item.__key : ""
                                }));
                            }}
                            isDisabled={!newEducationDraft?.facultyID}
                            data={(departments || []).map(d => ({
                                title: d.value,
                                value: d._id
                            }))}
                            titleExtractor={item => item.title}
                            keyExtractor={item => item.value}
                            selectSheetProps={{
                                portalName: "modal-system"
                            }}
                            spreadBehaviour="stretch"
                        />
                        <TextInput
                            type={newEducationDraft?.graduationYear && newEducationDraft.graduationYear.toString().length !== 4 ? "danger" : "default"}
                            title={localize("members-form-graduationyear") || "Mezuniyet Yılı"}
                            value={newEducationDraft?.graduationYear?.toString() || ""}
                            onChangeText={(text) => setNewEducationDraft(prev => ({
                                ...(prev || {
                                    graduationYear: 0,
                                    departmentID: "",
                                    universityID: "",
                                    facultyID: ""
                                }),
                                graduationYear: Number(text) || 0
                            }))}
                            spreadBehaviour="stretch"
                        />
                        <View
                            style={[
                                stylesheet.row,
                                {
                                    justifyContent: "flex-end"
                                }
                            ]}
                        >
                            <Button
                                isDisabled={!(newEducationDraft?.universityID && newEducationDraft?.facultyID && newEducationDraft?.departmentID && newEducationDraft?.graduationYear)}
                                onPress={() => {
                                    if (newEducationDraft) {
                                        if (editEducationIndex !== null) {
                                            setFormData(prev => ({
                                                ...prev,
                                                education: prev.education?.map((e, i) => i === editEducationIndex ? newEducationDraft : e)
                                            }));
                                        } else {
                                            setFormData(prev => ({
                                                ...prev,
                                                education: [
                                                    ...(prev.education || []),
                                                    newEducationDraft
                                                ]
                                            }));
                                        }
                                    }
                                    setIsEducationSheetVisible(false);
                                }}
                                icon={({
                                    color,
                                    size
                                }) => <SaveIcon
                                    color={colors.project.brandWhite || colors.content.icon[color]}
                                    size={size}
                                />}
                                title={localize("members-form-save") || "Kaydet"}
                                customTextColor={colors.project.brandWhite}
                                customBorderColor="gold"
                                spreadBehaviour="free"
                                customColor="gold"
                                variant="filled"
                            />
                        </View>
                    </View>
                </View>
            </ScrollView>
        </Modal>
    </Fragment>;
};

export default MemberEditModal;
